import {Avatar, Box, HStack, Icon, Text, VStack} from '@chakra-ui/react';
import {ChevronRightIcon} from '@chakra-ui/icons';
import {State} from '@synako/halitrek';
import {ParticipationDesc} from 'api-types/ffconnect-hal';
import {formatDateForConversation} from 'ff-common';
import {useTranslation} from 'react-i18next';

/**
 * Ligne d'une conversation dans la liste côté client.
 * Affiche l'avatar et le nom du dernier expéditeur + date + titre.
 */
export const ConversationClientLine: React.FC<{
  participationState: State<ParticipationDesc>;
  onClick?: () => void;
}> = ({participationState, onClick}) => {
  const {t} = useTranslation();
  const participation = participationState.data;

  // Vérifier s'il y a des messages non lus pour afficher la pastille bleue
  const hasMessagesNonlus = participation.messages_non_lus > 0;

  // Formatage de la date du dernier message
  const dateFormatee = formatDateForConversation(
    participation.conversation_dernier_message_le,
    t('client.conversations.hier'),
  );

  // Titre de la conversation
  const titre =
    participation.conversation_titre ?? t('client.conversations.titre_defaut');

  // Nom du dernier expéditeur (dénormalisé sur la participation)
  const nomComplet =
    participation.conversation_dernier_expediteur_nom_complet ?? '';

  return (
    <Box
      mb={3}
      mt={3}
      borderRadius='8px'
      cursor='pointer'
      _hover={{bg: 'gray.bg'}}
      onClick={onClick}
      w='100%'
    >
      <HStack
        spacing={3}
        p={1}
        align='center'
        w='100%'
      >
        {/* Avatar du dernier expéditeur */}
        <Avatar
          name={nomComplet}
          color='white'
          size='sm'
          ml={1}
        />

        {/* Contenu */}
        <VStack
          align='flex-start'
          spacing={0}
          flex={1}
          minW={0}
        >
          {/* Titre de la conversation */}
          <Text
            fontSize='sm'
            fontWeight='600'
            color='black.text'
            noOfLines={1}
          >
            {titre}
          </Text>

          {/* Nom du dernier expéditeur + date */}
          <HStack
            spacing={2}
            mt={1}
          >
            <Text
              fontSize='xs'
              color='gray.dark'
              fontWeight='600'
              noOfLines={1}
            >
              {nomComplet}
            </Text>
            <Text
              fontSize='xs'
              color='gray.dark'
              noOfLines={1}
            >
              {dateFormatee}
            </Text>
          </HStack>
        </VStack>

        {/* Badge avec le nombre de messages non lus */}
        {hasMessagesNonlus && (
          <Box
            minW='22px'
            h='22px'
            borderRadius='full'
            bg='blue.main'
            display='flex'
            alignItems='center'
            justifyContent='center'
            px={1.5}
          >
            <Text
              fontSize='12px'
              fontWeight='600'
              color='white'
            >
              {participation.messages_non_lus}
            </Text>
          </Box>
        )}

        {/* Flèche à droite */}
        <Icon
          as={ChevronRightIcon}
          boxSize={5}
          color='gray.dark'
        />
      </HStack>
    </Box>
  );
};
